import { createSelector, Selector } from '@reduxjs/toolkit'
import { GlobalState } from 'store'
import { getCurrentWindowId, createDeepEqualSelector } from 'utils'
import { selectionStoreSelector } from './storeSelectors'
import { GridId, GridSettingsId } from '../store/grid'
import { SubscriptionDetail, PreparedStagingOrder, WindowSelections } from 'store/selection/selectionTypes'
import {
  Organisation,
  EntityType,
  Collection,
  EntityTypeEnum,
  SubscriptionIdentifier,
  SubscriptionFilter,
  NegotiationInstruction
} from 'api'
import { createEntityGetterSelector } from './entitySelectors'
import { newEmptyNegotiationInstruction } from 'model'
import { userPreferenceSelector } from './userPreferenceSelectors'

export function createActivityMonitorOpenSelector(): Selector<GlobalState, boolean> {
  return createSelector(selectionStoreSelector, selectionStore => selectionStore.activityMonitorOpen)
}

export function createActivityMonitorLastClosedSelector(): Selector<GlobalState, Date> {
  return createSelector(selectionStoreSelector, selectionStore => selectionStore.activityMonitorLastClosed)
}

export function createGridColumnAutoSizedSelector<TProps>(
  gridIdSelector: Selector<{}, GridId, [TProps]>
): Selector<GlobalState, boolean, [TProps]> {
  return createSelector(selectionStoreSelector, gridIdSelector, (selectionStore, gridId) => selectionStore.gridColumnsAutoSized[gridId])
}

export function createWindowSelectionsSelector(): Selector<GlobalState, WindowSelections> {
  return createSelector(selectionStoreSelector, selectionStore => selectionStore.windows[getCurrentWindowId()])
}

export const preparedStagingOrdersSelector: Selector<GlobalState, PreparedStagingOrder[]> = createDeepEqualSelector(
  createWindowSelectionsSelector(),
  windowSelections => windowSelections.preparedStagingOrders
)

export const preparedNegotiationInstructionSelector: Selector<GlobalState, NegotiationInstruction> = createSelector(
  [preparedStagingOrdersSelector, userPreferenceSelector],
  (preparedStagingOrders, userPreference) => {
    return preparedStagingOrders.length ? preparedStagingOrders[0].negotiationInstruction : newEmptyNegotiationInstruction(userPreference)
  }
)

export const preparedStagingOrderIdsSelector: Selector<GlobalState, string[]> = createSelector(
  preparedStagingOrdersSelector,
  preparedStagingOrders => preparedStagingOrders.map(preparedStagingOrder => preparedStagingOrder.orderDetail.order.orderId)
)

export function createPreparedCollectionSelector(): Selector<GlobalState, Collection | null> {
  return createSelector(
    createWindowSelectionsSelector(),
    createEntityGetterSelector(EntityType.COLLECTION),
    (windowSelections, getCollection) =>
      windowSelections.preparedCollectionId ? getCollection(windowSelections.preparedCollectionId) : null
  )
}

export function createPreparedCollectionIdSelector(): Selector<GlobalState, string | null> {
  return createDeepEqualSelector(createWindowSelectionsSelector(), windowSelections => windowSelections.preparedCollectionId)
}

export function createSelectedGridSettings(): Selector<GlobalState, Record<GridId, GridSettingsId>> {
  return createDeepEqualSelector(createWindowSelectionsSelector(), windowSelections => windowSelections.selectedGridSettings)
}

export function createLeftNavigationMinimisedSelector(): Selector<GlobalState, boolean> {
  return createDeepEqualSelector(createWindowSelectionsSelector(), windowSelection => windowSelection.leftNavigationMinimised)
}

export function createRFQPanelIsPinnedSelector(): Selector<GlobalState, boolean> {
  return createDeepEqualSelector(createWindowSelectionsSelector(), windowSelections => windowSelections.prepareRFQPanelPinned)
}

export const openOrganisationIdsSelector: Selector<GlobalState, string[]> = createSelector(
  createWindowSelectionsSelector(),
  windowSelections => windowSelections.openOrganisationIds
)

export const openOrganisationsSelector: Selector<GlobalState, Organisation[]> = createSelector(
  openOrganisationIdsSelector,
  createEntityGetterSelector(EntityType.ORGANISATION),
  (openOrganisationIds, organisationGetter) => {
    return openOrganisationIds
      .map(organisationId => organisationGetter(organisationId))
      .filter(organisation => organisation !== null) as Organisation[]
  }
)

const selectedCollectionIdsSelector: Selector<GlobalState, Record<GridId, string>> = createDeepEqualSelector(
  createWindowSelectionsSelector(),
  windowSelections => windowSelections.selectedCollectionIds
)

export const createSelectedCollectionIdSelector = (gridId: GridId | null): Selector<GlobalState, string | null> => {
  return gridId === null
    ? () => null
    : createSelector(selectedCollectionIdsSelector, selectedCollectionIds => {
        return selectedCollectionIds[gridId] ? selectedCollectionIds[gridId] : null
      })
}

export const createSelectedCounterpartyIdSelector = (gridId: GridId | null): Selector<GlobalState, string | null> => {
  return gridId === null
    ? () => null
    : createSelector(createWindowSelectionsSelector(), windowSelections => {
        return windowSelections.selectedCounterpartyIds[gridId] ? windowSelections.selectedCounterpartyIds[gridId] : null
      })
}

const collapsedNavigationEntriesDictSelector: Selector<GlobalState, Record<string, boolean>> = createDeepEqualSelector(
  createWindowSelectionsSelector(),
  windowSelections => windowSelections.collapsedNavigationEntries
)

export const navigationCollapsedGetterSelector: Selector<GlobalState, (navigationEntry: string) => boolean> = createSelector(
  collapsedNavigationEntriesDictSelector,
  collapsedNavigationEntries => (navigationEntry: string) => {
    const a = collapsedNavigationEntries[navigationEntry] ? collapsedNavigationEntries[navigationEntry] : false
    return a
  }
)

export const pendingCollectionCorrelationIdSelector = createSelector(
  createWindowSelectionsSelector(),
  windowSelections => windowSelections.pendingCollectionCorrelationId
)

export const createGridActivityHighlightSelector = (gridId: GridId) =>
  createSelector(createWindowSelectionsSelector(), windowSelections => windowSelections.gridActivityHighlight[gridId])

export const currentGridIdSelector: Selector<GlobalState, GridId | null> = createSelector(
  createWindowSelectionsSelector(),
  windowSelections => windowSelections.currentGridId
)

export const gridRowIdInEditSelector: Selector<GlobalState, string | null> = createSelector(
  createWindowSelectionsSelector(),
  windowSelections => windowSelections.gridRowIdInEdit
)

export const gridRowInEditSelector: Selector<GlobalState, string | null> = createSelector(
  createWindowSelectionsSelector(),
  windowSelections => windowSelections.gridRowInEdit
)

export const dealIdForRelatedDealTradesSelector: Selector<GlobalState, string | null> = createSelector(
  createWindowSelectionsSelector(),
  windowSelections => windowSelections?.dealIdForRelatedDealTrades
)

export const dealIdForRelatedOpenRepoTradesSelector: Selector<GlobalState, string | null> = createSelector(
  createWindowSelectionsSelector(),
  windowSelections => windowSelections?.dealIdForRelatedOpenRepoTrades
)

export const dealIdForRelatedStructuredTradesSelector: Selector<GlobalState, string | null> = createSelector(
  createWindowSelectionsSelector(),
  windowSelections => windowSelections?.dealIdForRelatedStructuredTrades
)

export const dealIdForRelatedMidTradeInitiatedTradesSelector: Selector<GlobalState, string | null> = createSelector(
  createWindowSelectionsSelector(),
  windowSelections => windowSelections?.dealIdForRelatedMidTradeInitiatedTrades
)

export const dealIdForRelatedMidTradeIncomingTradesSelector: Selector<GlobalState, string | null> = createSelector(
  createWindowSelectionsSelector(),
  windowSelections => windowSelections?.dealIdForRelatedMidTradeIncomingTrades
)

export function createEntitySubscriptionSelector<TProps>(
  entityTypeSelector: Selector<{}, EntityTypeEnum, [TProps]>,
  subscriptionIdentifierSelector: Selector<{}, SubscriptionIdentifier, [TProps]>
): Selector<GlobalState, SubscriptionDetail | undefined> {
  return createSelector(
    selectionStoreSelector,
    entityTypeSelector,
    subscriptionIdentifierSelector,
    (selectionStore, entityType, subscriptionIdentifier) => selectionStore.entitySubscriptions[entityType]?.[subscriptionIdentifier]
  )
}

export function createEntitySubscriptionFiltersSelector<TProps>(
  entityTypeSelector: Selector<{}, EntityTypeEnum, [TProps]>,
  subscriptionIdentifierSelector: Selector<{}, SubscriptionIdentifier, [TProps]>
): Selector<GlobalState, SubscriptionFilter[]> {
  return createSelector(
    [createEntitySubscriptionSelector(entityTypeSelector, subscriptionIdentifierSelector)],
    entitySubscription => entitySubscription?.filters ?? []
  )
}
